import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Mail, Building2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Layout } from "@/components/Layout";

// Phase 3 (A1): admin surface for provisioning external advisers.
//
// Admin-gated (canAccessAdmin, exactly as ClientDocsManager). Lets an admin:
//   - create / pick an adviser firm (adviser_firms)
//   - issue an adviser invite for an email (calls the staff-gated `adviser-invite` edge fn)
//   - review existing firms, invites and links
//
// EVERYTHING degrades gracefully until the backend lands: the adviser_firms / adviser_invites /
// adviser_links tables ship in m30 (unapplied) and the `adviser-invite` function ships in E1
// (undeployed). Every query/invoke swallows errors and shows an empty/soft state, so this page
// renders cleanly today and lights up once Sam applies m30 + deploys E1.

interface AdviserFirm {
  id: string;
  name: string;
  fca_ref: string | null;
  status: string;
  entitlement_band: string;
}

interface AdviserInvite {
  id: string;
  email: string;
  firm_id: string | null;
  status: string;
  created_at: string | null;
  expires_at: string | null;
}

interface AdviserLinkRow {
  id: string;
  user_id: string;
  firm_id: string | null;
  status: string;
}

// The three tables are not yet in the generated Supabase types (they land in m30), so we use
// the repo's established `.from('table' as any)` escape hatch.
/* eslint-disable @typescript-eslint/no-explicit-any */
const from = (table: string) => supabase.from(table as any);
/* eslint-enable @typescript-eslint/no-explicit-any */

export default function AdvisersAdmin() {
  const { canAccessAdmin, loading: authLoading } = useAuth();
  const [firms, setFirms] = useState<AdviserFirm[]>([]);
  const [invites, setInvites] = useState<AdviserInvite[]>([]);
  const [links, setLinks] = useState<AdviserLinkRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [showFirmDialog, setShowFirmDialog] = useState(false);
  const [newFirm, setNewFirm] = useState({ name: "", fca_ref: "", entitlement_band: "standard" });
  const [savingFirm, setSavingFirm] = useState(false);

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirmId, setInviteFirmId] = useState<string>("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const isAdmin = canAccessAdmin();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [firmRes, inviteRes, linkRes] = await Promise.all([
        from("adviser_firms").select("id, name, fca_ref, status, entitlement_band").order("name", { ascending: true }),
        from("adviser_invites").select("id, email, firm_id, status, created_at, expires_at").order("created_at", { ascending: false }),
        from("adviser_links").select("id, user_id, firm_id, status"),
      ]);
      setFirms(((firmRes as any).error ? [] : (firmRes as any).data) || []);
      setInvites(((inviteRes as any).error ? [] : (inviteRes as any).data) || []);
      setLinks(((linkRes as any).error ? [] : (linkRes as any).data) || []);
    } catch {
      setFirms([]);
      setInvites([]);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      void fetchAll();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading, isAdmin, fetchAll]);

  const handleCreateFirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFirm.name.trim()) {
      toast.error("Firm name is required");
      return;
    }
    setSavingFirm(true);
    try {
      const { error } = await from("adviser_firms").insert({
        name: newFirm.name.trim(),
        fca_ref: newFirm.fca_ref.trim() || null,
        status: "active",
        entitlement_band: newFirm.entitlement_band,
      } as any);
      if (error) throw error;
      toast.success("Firm created");
      setShowFirmDialog(false);
      setNewFirm({ name: "", fca_ref: "", entitlement_band: "standard" });
      void fetchAll();
    } catch (err: any) {
      // Most likely cause before m30 is applied: the table doesn't exist yet.
      toast.error(err?.message || "Could not create firm (backend not provisioned yet).");
    } finally {
      setSavingFirm(false);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error("Adviser email is required");
      return;
    }
    setSendingInvite(true);
    try {
      // The staff-gated edge function is the only creator of invites (E1). It is not deployed
      // until Sam runs the gate; a failure here just means "not provisioned yet".
      const { error } = await supabase.functions.invoke("adviser-invite", {
        body: {
          email: inviteEmail.trim(),
          firm_id: inviteFirmId || null,
        },
      });
      if (error) throw error;
      toast.success("Invite sent");
      setShowInviteDialog(false);
      setInviteEmail("");
      setInviteFirmId("");
      void fetchAll();
    } catch (err: any) {
      toast.error(err?.message || "Could not send invite (invite function not deployed yet).");
    } finally {
      setSendingInvite(false);
    }
  };

  const firmName = (firmId: string | null) => firms.find((f) => f.id === firmId)?.name ?? "—";

  if (authLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
              <CardDescription>Checking your permissions...</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-destructive">Access Denied</CardTitle>
              <CardDescription>You need admin privileges to access this page.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold">Advisers</h1>
              <p className="text-muted-foreground">
                Provision external adviser firms and issue portal invitations.
              </p>
            </div>
            <div className="flex gap-2">
              <Dialog open={showFirmDialog} onOpenChange={setShowFirmDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Building2 className="w-4 h-4 mr-2" />
                    New Firm
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Adviser Firm</DialogTitle>
                    <DialogDescription>
                      The parent firm an adviser account is linked to.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateFirm} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="firm-name">Firm Name</Label>
                      <Input
                        id="firm-name"
                        value={newFirm.name}
                        onChange={(e) => setNewFirm((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Example IFA Ltd"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="firm-fca">FCA Reference (optional)</Label>
                      <Input
                        id="firm-fca"
                        value={newFirm.fca_ref}
                        onChange={(e) => setNewFirm((p) => ({ ...p, fca_ref: e.target.value }))}
                        placeholder="123456"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="firm-band">Entitlement Band</Label>
                      <Select
                        value={newFirm.entitlement_band}
                        onValueChange={(v) => setNewFirm((p) => ({ ...p, entitlement_band: v }))}
                      >
                        <SelectTrigger id="firm-band">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="lite">Lite</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowFirmDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={savingFirm}>
                        {savingFirm ? "Saving..." : "Create Firm"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Invite Adviser
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Invite an Adviser</DialogTitle>
                    <DialogDescription>
                      Sends a signup link the adviser redeems to link their account to a firm.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSendInvite} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-email">Adviser Email</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="adviser@firm.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invite-firm">Firm</Label>
                      <Select value={inviteFirmId} onValueChange={setInviteFirmId}>
                        <SelectTrigger id="invite-firm">
                          <SelectValue placeholder="Select a firm (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          {firms.map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setShowInviteDialog(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={sendingInvite}>
                        {sendingInvite ? "Sending..." : "Send Invite"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Firms */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Firms ({firms.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : firms.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No adviser firms yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>FCA Ref</TableHead>
                        <TableHead>Band</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {firms.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell className="font-medium">{f.name}</TableCell>
                          <TableCell className="font-mono text-sm">{f.fca_ref || "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{f.entitlement_band}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={f.status === "active" ? "default" : "secondary"}>
                              {f.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invites */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Invites ({invites.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : invites.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No invites yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Firm</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Expires</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invites.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.email}</TableCell>
                          <TableCell>{firmName(inv.firm_id)}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                inv.status === "accepted"
                                  ? "default"
                                  : inv.status === "pending"
                                    ? "secondary"
                                    : "outline"
                              }
                            >
                              {inv.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Links */}
          <Card>
            <CardHeader>
              <CardTitle>Linked Advisers ({links.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : links.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No advisers linked yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User ID</TableHead>
                        <TableHead>Firm</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="font-mono text-xs">{l.user_id}</TableCell>
                          <TableCell>{firmName(l.firm_id)}</TableCell>
                          <TableCell>
                            <Badge variant={l.status === "active" ? "default" : "secondary"}>
                              {l.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
